/**
 * General utility functions
 */

/**
 * Recursively removes all undefined keys from an object
 * Also removes empty objects (objects with no keys after removing undefined values)
 */
export const deleteUndefinedKeys = (obj: any): any => {
    if (!obj || typeof obj !== 'object') {
        return obj;
    }

    if (Array.isArray(obj)) {
        return obj.map(item => deleteUndefinedKeys(item));
    }

    const filtered = Object.fromEntries(
        Object.entries(obj)
            .filter(([_, value]) => value !== undefined)
            .map(([key, value]) => {
                const processed = deleteUndefinedKeys(value);
                // Remove key if nested object is now empty
                if (
                    processed &&
                    typeof processed === 'object' &&
                    !Array.isArray(processed) &&
                    Object.keys(processed).length === 0
                ) {
                    return null;
                }
                return [key, processed];
            })
            .filter(entry => entry !== null) as [string, any][],
    );

    return filtered;
};

const PRODUCTION_TIERS = ['go', 'next', 'practice'];

/**
 * Numbered shards of shared desktop tiers collapse to one identity
 * (go2 -> go, next1 -> next, stage-nextgen -> stage).
 */
function environmentTier(hostname: string): string {
    const label = (hostname.split('.')[0] ?? '').replace(/-nextgen$/, '');
    return label.replace(/-?\d+$/, '');
}

function isStageHostname(hostname: string): boolean {
    return (
        environmentTier(hostname) === 'stage' ||
        hostname.endsWith('.stage.servicetitan.com') ||
        hostname === 'customerportal-stage.servicetitan.com' ||
        hostname.endsWith('.customerportal-stage.servicetitan.com') ||
        hostname.endsWith('.stage.devtest.financing.srv.st.dev')
    );
}

function isProductionHostname(hostname: string): boolean {
    return (
        PRODUCTION_TIERS.includes(environmentTier(hostname)) ||
        hostname.startsWith('go.') ||
        hostname.endsWith('.myservicetitan.com') ||
        hostname.endsWith('.go.servicetitan.com')
    );
}

/**
 * Gets the current MFE version based on the hostname
 */
export function getMfeVersion(): string {
    const hostname = window.location.hostname;

    if (isProductionHostname(hostname)) {
        return '@latest';
    }

    if (
        hostname === 'localhost' ||
        hostname.endsWith('.localhost') ||
        hostname.startsWith('127.0.0.1')
    ) {
        return 'local';
    }

    if (isStageHostname(hostname)) {
        return '@stage';
    }

    return '@qa';
}

/**
 * Checks if a value is null, undefined, or an empty string
 */
export const isBlank = (value: string | null | undefined): boolean => {
    return value === null || value === undefined || value.trim() === '';
};

/**
 * Creates a stable key from a driverIds array by sorting and comma-joining.
 * Used to group rules that share the same set of business units.
 */
export function getDriverIdsKey(driverIds: (string | number)[]): string {
    return [...driverIds].map(String).sort().join(',');
}
